import { useState } from "react";
import styles from "./StoreSettings.module.scss";
import Button from "../../components/Button/Button";
import { deleteStore, Store } from "../../ApiService";
import { Form, FormikProvider, useFormik } from "formik";
import TextField from "../../components/TextField/TextField";
import { updateStore } from "../../ApiService";
import { useMutation, useQueryClient } from "react-query";
import { removeEmptyProps } from "../../util/util";
import Modal from "../../components/Modal/Modal";
import { Checkbox } from "../../components/Checkbox/Checkbox";

import useTokenValidation from "./useTokenValidation";
import useOzonCredentialsValidation from "./useOzonCredentialsValidation";
import { useNavigate } from "react-router-dom";
import ButtonBack from "../../components/ButtonBack/ButtonBack";
import PageBlock from "../../components/PageContainer/PageBlock";
import Stars from "../../components/Star/Stars";

function StoreSettings({ store }: { store: Store }) {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalDelete, setIsModalDelete] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const isWB = store.marketplace === 'wb';

  const updateQuery = useMutation((values: Store) => updateStore(values), {
    onSuccess: (updatedStore) => {
      queryClient.setQueryData(["store", store.id], updatedStore);
      setErrorMessage(null);
      setIsModalOpen(true);
    },
    onError: (error) => {
      console.error('Failed to update store:', error);
      setErrorMessage(`Ошибка при обновлении данных магазина: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
    }
  });

  const deleteQuery = useMutation((id: string) => deleteStore(id), {
    onSuccess: () => {
      queryClient.refetchQueries("stores").then(() => navigate("/stores"));
    },
  });

  const handleDeleteStore = () => {
    deleteQuery.mutate(store.id);
  };

  const formik = useFormik({
    initialValues: {
      name: store.name,
      prompt: store.prompt || {},
      token: store.token || '',
      clientId: store.clientId || '',
      apiKey: store.apiKey || '',
      generateEmptyFeedback: store.generateEmptyFeedback || false,
      smartAutoReplies: store.smartAutoReplies || false,
      id: store.id,
      marketplace: store.marketplace,
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      if (JSON.stringify(values) !== JSON.stringify({ ...store, id: store.id })) {
        // Для WB устанавливаем apiKey в null, так как он не нужен
        const finalValues = { ...values };
        if (finalValues.marketplace === 'wb') {
          finalValues.apiKey = '';
        }
        console.log('Submitting store update:', { ...finalValues, apiKey: finalValues.apiKey ? '[HIDDEN]' : undefined });
        updateQuery.mutate({ ...finalValues, id: store.id });
      } else {
        console.log('No changes detected, skipping update');
        setIsModalOpen(true);
      }
    },
  });

  const {
    values: { prompt, name, token, generateEmptyFeedback, clientId, apiKey, smartAutoReplies },
    handleChange,
    setFieldValue,
    submitForm,
  } = formik;

  const { isTokenEditing, isTokenValid, handleTokenChange, setEditChecked } =
    useTokenValidation(setFieldValue);

  const { isCredentialsValid, handleClientIdChange, handleApiKeyChange } =
    useOzonCredentialsValidation(setFieldValue, { clientId, apiKey });

  const handleBackButtonClick = () => {
    navigate("/stores");
  };

  return (
    <PageBlock>
      <div className="flex items-center">
        <ButtonBack onClick={handleBackButtonClick} />

        <div className="flex flex-1 items-center justify-center">
          <div className={styles.title}>{store.name}</div>
        </div>
      </div>
      <FormikProvider value={formik}>
        <Form>
          <div className="flex flex-col gap-4">
            <div>
              <TextField
                name="name"
                label="Как называется ваш магазин?"
                type="text"
                placeholder="Название магазина"
                onChange={handleChange}
                value={name}
              />
              {isWB ? (
                <>
                  <TextField
                    name="token"
                    label="WB Ключ. Вставьте новый ключ, или не трогайте это поле"
                    type="text"
                    placeholder="WB Ключ"
                    onChange={handleTokenChange}
                    value={token}
                    disabled={!isTokenEditing}
                    valid={isTokenEditing && !!token ? isTokenValid : undefined}
                  />
                  <Checkbox
                    label="Редактировать ключ"
                    checked={isTokenEditing}
                    setChecked={setEditChecked}
                  />
                </>
              ) : (
                <>
                  <TextField
                    name="clientId"
                    label="ID кабинета Ozon (Client ID)"
                    type="text"
                    placeholder="Введите Client ID"
                    onChange={handleClientIdChange}
                    value={clientId || ""}
                    autoComplete="off"
                    valid={!!clientId && !!apiKey ? isCredentialsValid : undefined}
                  />
                  <TextField
                    name="apiKey"
                    label="API ключ вашего магазина Ozon"
                    type="text"
                    placeholder="Введите API ключ"
                    onChange={handleApiKeyChange}
                    value={apiKey || ""}
                    autoComplete="off"
                    valid={!!clientId && !!apiKey ? isCredentialsValid : undefined}
                  />
                </>
              )}
            </div>
            <div className="border-t border-gray-300 shadow-md my-4"></div>
            {/* <TextField
              name="prompt.storeAssortment"
              label="Какой ассортимент товаров представлен в вашем магазине? Пожалуйста, опишите его кратко."
              type="text"
              placeholder="Ассортимент товаров"
              onChange={handleChange}
              value={prompt.storeAssortment}
            /> */}
            <TextField
              name="prompt.bestsellerList"
              label="Пожалуйста, предоставьте список ваших бестселлеров (Напишите список SKU)"
              type="text"
              placeholder="Бестселлеры магазина"
              onChange={handleChange}
              value={prompt.bestsellerList}
            />
            <TextField
              name="prompt.storeFeatures"
              label="Есть ли у вашего бренда особые особенности или политика, которые вы хотели бы подчеркнуть в ответах на отзывы? Если да, пожалуйста, укажите их."
              type="text"
              placeholder="Особенности бренда или политики"
              onChange={handleChange}
              value={prompt.storeFeatures}
            />
            <TextField
              name="prompt.recommendations"
              label="Есть ли у вас рекомендации для клиентов при возникновении проблем (например, проверять товар до выхода из пункта выдачи)?"
              type="text"
              placeholder="Рекомендации для клиентов"
              onChange={handleChange}
              value={prompt.recommendations}
            />
            <TextField
              name="prompt.greeting"
              label="Какое приветствие вы предпочитаете использовать в ответах? (например, 'Здравствуйте' или 'Привет')"
              type="text"
              placeholder="Приветствие"
              onChange={handleChange}
              value={prompt.greeting}
            />
            <TextField
              name="prompt.closing"
              label="Какую завершающую фразу вы хотели бы использовать в ответах?"
              type="text"
              placeholder="Завершающая фраза"
              onChange={handleChange}
              value={prompt.closing}
            />
            <TextField
              name="prompt.tone"
              label="Какие эмоции или настроение вы хотели бы передавать в ответах (например, дружелюбие, профессионализм)?"
              type="text"
              placeholder="Эмоции и настроение в ответах"
              onChange={handleChange}
              value={prompt.tone}
            />
            <TextField
              name="prompt.addressing"
              label="Как вы предпочитаете обращаться к клиентам (на 'вы' или на 'ты')?"
              type="text"
              placeholder="Вид обращения"
              onChange={handleChange}
              value={prompt.addressing}
            />
            <TextField
              name="prompt.specInstructions"
              label="Есть ли у вас какие-либо особые инструкции или пожелания по формулировкам в ответах на отзывы?"
              type="text"
              placeholder="Особые инструкции или пожелания"
              onChange={handleChange}
              value={prompt.specInstructions}
            />
            <TextField
              name="prompt.emojiSet"
              label="Какие эмодзи использовать в конце ответов? (через запятую, например: 🌸, 💖, 🌷)"
              type="text"
              placeholder="Набор эмодзи"
              onChange={handleChange}
              value={prompt.emojiSet}
            />
            <div className="border-t border-gray-300 shadow-md my-4"></div>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  id="generateEmptyFeedback"
                  checked={generateEmptyFeedback}
                  onChange={(e) => {
                    setFieldValue("generateEmptyFeedback", e.target.checked);
                  }}
                  className="w-4 h-4"
                />
                <label htmlFor="generateEmptyFeedback">
                  Использовать шаблоны ответов для пустых отзывов
                </label>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  id="smartAutoReplies"
                  checked={smartAutoReplies || false}
                  onChange={(e) => {
                    setFieldValue("smartAutoReplies", e.target.checked);
                  }}
                  className="w-4 h-4"
                />
                <label htmlFor="smartAutoReplies">
                  Использовать умные автоответы
                </label>
              </div>

              {generateEmptyFeedback && (
                <>
                  <span className="block mb-4">Стандартные ответы на отзывы без текста:</span>
                  <TextField
                    name="prompt.noTextOneStar"
                    label="Ответ на отзыв с 1 звездой без текста"
                    type="text"
                    placeholder="Ответ на отзыв с 1 звездой"
                    onChange={handleChange}
                    value={prompt.noTextOneStar}
                  />
                  <TextField
                    name="prompt.noTextTwoStar"
                    label="Ответ на отзыв с 2 звездами без текста"
                    type="text"
                    placeholder="Ответ на отзыв с 2 звездами"
                    onChange={handleChange}
                    value={prompt.noTextTwoStar}
                  />
                  <TextField
                    name="prompt.noTextThreeStar"
                    label="Ответ на отзыв с 3 звездами без текста"
                    type="text"
                    placeholder="Ответ на отзыв с 3 звездами"
                    onChange={handleChange}
                    value={prompt.noTextThreeStar}
                  />
                  <TextField
                    name="prompt.noTextFourStar"
                    label="Ответ на отзыв с 4 звездами без текста"
                    type="text"
                    placeholder="Ответ на отзыв с 4 звездами"
                    onChange={handleChange}
                    value={prompt.noTextFourStar}
                  />
                  <TextField
                    name="prompt.noTextFiveStar"
                    label="Ответ на отзыв с 5 звездами без текста"
                    type="text"
                    placeholder="Ответ на отзыв с 5 звездами"
                    onChange={handleChange}
                    value={prompt.noTextFiveStar}
                  />
                </>
              )}
            </div>
          </div>
        </Form>
      </FormikProvider>

      {errorMessage && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded my-4" role="alert">
          <strong className="font-bold">Ошибка!</strong>
          <span className="block sm:inline"> {errorMessage}</span>
        </div>
      )}

      <div className="flex justify-between mt-4">
        <Button title="Обновить данные" onClick={submitForm} />
        <Button
          title="Удалить магазин"
          status="alert"
          onClick={() => setIsModalDelete(true)}
        />
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <p className={styles.h3Font}>Данные магазина успешно обновлены!</p>
        <div className="flex flex-col items-center mt-4">
          <Button onClick={() => setIsModalOpen(false)} title="ОК" />
        </div>
      </Modal>

      <Modal isOpen={isModalDelete} onClose={() => setIsModalDelete(false)}>
        <p className={styles.h3Font}>
          {`Вы действительно хотите удалить магазин ${store.name}?`}
        </p>
        <div className="flex justify-around mt-4">
          <Button title="Удалить" status="alert" onClick={handleDeleteStore} />
          <Button title="Отмена" onClick={() => setIsModalDelete(false)} />
        </div>
      </Modal>
    </PageBlock>
  );
}

export default StoreSettings;
